import type { HGMediaType } from "@/common/types";

// Longest single line still treated as a title rather than body text
const MAX_TITLE_LENGTH = 100;

export function isProbablyUrl(text: string): boolean {
  const t = text.trim();
  if (!t || /\s/.test(t)) return false;
  if (/^https?:\/\/\S+$/i.test(t)) return true;
  // bare domain like example.com or example.com/path
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+(\/\S*)?$/i.test(t);
}

export interface PastedTextFields {
  title?: string;
  content?: string;
  url?: string;
}

export function classifyPastedText(text: string): PastedTextFields {
  const t = text.trim();
  if (!t) return {};
  if (isProbablyUrl(t)) return { url: t };

  const newline = t.indexOf("\n");
  if (newline === -1) {
    return t.length <= MAX_TITLE_LENGTH ? { title: t } : { content: t };
  }

  const firstLine = t.slice(0, newline).trim();
  const rest = t.slice(newline + 1).trim();
  if (firstLine.length > 0 && firstLine.length <= MAX_TITLE_LENGTH) {
    return { title: firstLine, content: rest };
  }
  return { content: t };
}

function extForMime(type: string): string {
  const subtype = type.split("/")[1]?.toLowerCase() ?? "";
  if (subtype === "jpeg") return "jpg";
  if (subtype === "svg+xml") return "svg";
  return subtype || "bin";
}

function hasExtension(name: string): boolean {
  const dot = name.lastIndexOf(".");
  return dot > 0 && dot < name.length - 1;
}

// Pulls real files out of a paste. Clipboard image blobs often have no name (or
// no extension), which the backend rejects since it validates by filename suffix
// — synthesize a name with a proper extension derived from the MIME type.
export function clipboardMediaFiles(cd: DataTransfer): File[] {
  const raw: File[] = [];
  if (cd.files && cd.files.length > 0) {
    raw.push(...Array.from(cd.files));
  } else if (cd.items && cd.items.length > 0) {
    for (const item of Array.from(cd.items)) {
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) raw.push(file);
      }
    }
  }

  return raw.map((file) => {
    if (file.name && hasExtension(file.name)) return file;
    const name = `pasted-${Date.now()}.${extForMime(file.type)}`;
    return new File([file], name, { type: file.type });
  });
}

export function mediaTypeForFile(file: File): HGMediaType {
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("audio/")) return "audio";
  return "document";
}

// True when the paste target is a text field the user is actively editing, so a
// paste should land there natively rather than being intercepted.
export function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  return el.isContentEditable;
}
